import { signBotSignature } from "./bridge"

interface SignatureRequest {
  method: string
  url: string
  body?: string
}

interface SignatureResponse {
  timestamp: string
  signature: string
}

interface SignatureCallback {
  resolve: (response: SignatureResponse) => void
  reject: (error: Error) => void
}

class SignatureManager {
  private pendingCallbacks = new Map<string, SignatureCallback>()
  private callbackCounter = 0

  private handleCallback(callbackId: string, timestamp: string, signature: string | null, error?: string) {
    const callback = this.pendingCallbacks.get(callbackId)
    if (!callback) return

    this.pendingCallbacks.delete(callbackId)

    if (error || !signature) {
      callback.reject(new Error(error || "Failed to get signature"))
    } else {
      callback.resolve({ timestamp, signature })
    }
  }

  async getSignature(request: SignatureRequest): Promise<SignatureResponse> {
    const botId = import.meta.env.VITE_BOT_ID
    if (!botId) {
      throw new Error("VITE_BOT_ID is not configured")
    }

    return new Promise((resolve, reject) => {
      const callbackId = `signature_${++this.callbackCounter}_${Date.now()}`

      this.pendingCallbacks.set(callbackId, { resolve, reject })

      // Create a unique callback function name for this request
      const callbackFunctionName = `handleSignatureCallback_${callbackId}`

      // Register the callback function globally
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any)[callbackFunctionName] = (timestamp: string, signature: string) => {
        this.handleCallback(callbackId, timestamp, signature)
        // Clean up the global function
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (window as any)[callbackFunctionName]
      }

      try {
        const url = new URL(request.url)
        const urlPath = url.pathname + url.search
        const args = [botId, false, request.method, urlPath, request.body || "", callbackFunctionName] as [string, boolean, string, string, string, string]

        signBotSignature(args)
      } catch (err) {
        console.error("Error invoking native signature handler:", err)
        this.pendingCallbacks.delete(callbackId)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (window as any)[callbackFunctionName]
        reject(err)
      }
    })
  }
}

export const signatureManager = new SignatureManager()
